/**
 * PropertyMedia — the deal room's top row: a live Street View you can walk
 * around, and the property's own pictures beside it.
 *
 * The static frame this replaces was a thumbnail of a building. Useful, but it
 * answers one question and stops. A live panorama answers the ones people
 * actually ask of a photo — what is next door, how does the loading face the
 * street, is that a shared yard, what does the approach look like — without
 * anyone leaving the deal.
 *
 * The panorama is created once and then MOVED rather than rebuilt when the
 * view changes. Constructing a StreetViewPanorama is expensive and each one
 * holds a WebGL context; building a fresh one on every toggle leaks them until
 * the browser starts dropping the oldest, which shows up as a panorama that
 * silently stops rendering several deals later.
 *
 * A deal with no coordinate, or no imagery at its coordinate, gets the photo
 * alone and no empty black box.
 */

import { useEffect, useRef, useState } from "react";
import * as B from "../common/brand";

// Below this, a heading aimed at the building is a picture of its wall. Above
// it, the building is far enough away to actually be in frame. 35m is roughly
// the width of a service road plus a setback - measured against the deals this
// was built for, not chosen for neatness.
const FACE_BUILDING_FROM_M = 35;

// WHICH IMAGERY. Not OUTDOOR, which is the obvious choice and the wrong one.
// OUTDOOR means "not indoors"; it does NOT mean "shot by the Street View car",
// so it happily returns a photosphere uploaded by a virtual-tour company -
// Threshold 360, Instant360, and a dozen photographers by name. 47 of 450
// captured deals came back that way, and in the live panorama their tiles do
// not even render: the frame is simply black.
//
// GOOGLE restricts the search to Google's own collection. Checked against the
// three worst offenders - Brickell Bay, 3500 45th St, 1200 Clint Moore - all
// three are black under OUTDOOR and all three show the building from the road
// under GOOGLE.
//
// Guarded because the enum is a relatively recent addition: on an older Maps
// build it is undefined, and passing undefined as `source` would widen the
// search to include indoor panoramas, which is worse than where we started.
const SOURCE = (g) => g.StreetViewSource.GOOGLE || g.StreetViewSource.OUTDOOR;

export default function PropertyMedia({
  lat, lng, photoUrl, address, onChangePhoto, readOnly = true, height = 380,
}) {
  const hostRef = useRef(null);
  const panoRef = useRef(null);
  const [status, setStatus] = useState("loading");   // loading | ok | none
  const [tab, setTab] = useState("street");

  const hasCoord = Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));

  useEffect(() => {
    if (!hasCoord) { setStatus("none"); return; }
    let cancelled = false;
    let tries = 0;

    // The Maps script is loaded once, asynchronously, by App.js. On a cold
    // load the deal room can render before it lands, so wait for it rather
    // than deciding there is no Street View.
    function start() {
      if (cancelled) return;
      const g = window.google && window.google.maps;
      if (!g) {
        if (tries++ > 60) setStatus("none");     // ~15s, then give up quietly
        else setTimeout(start, 250);
        return;
      }
      const at = { lat: Number(lat), lng: Number(lng) };
      new g.StreetViewService().getPanorama(
        { location: at, radius: 120, source: SOURCE(g) },
        (data, s) => {
          if (cancelled) return;
          if (s !== "OK" || !data?.location?.latLng) { setStatus("none"); return; }

          // WHICH WAY TO LOOK. Aiming at the deal's coordinate sounds
          // obviously right and often is not: the coordinate is the roof, so
          // when the nearest panorama is in the property's own yard you get a
          // close-up of a blank wall. Northway's nearest outdoor panorama is
          // 26m from the roof point and aiming at it renders exactly that,
          // while the car's own heading shows the yard, the parking and the
          // approach.
          //
          // So: aim at the building only from far enough back to see it, and
          // otherwise take the heading the Street View car was facing. Either
          // way the panorama is live, so anyone can drag or walk from there.
          const sph = g.geometry && g.geometry.spherical;
          const target = new g.LatLng(at.lat, at.lng);
          const away = sph ? sph.computeDistanceBetween(data.location.latLng, target) : 0;
          const carHeading = (data.tiles && data.tiles.centerHeading) || 0;
          const heading = (sph && away >= FACE_BUILDING_FROM_M)
            ? sph.computeHeading(data.location.latLng, target)
            : carHeading;

          if (panoRef.current) {
            panoRef.current.setPosition(data.location.latLng);
            panoRef.current.setPov({ heading, pitch: 0 });
          } else if (hostRef.current) {
            panoRef.current = new g.StreetViewPanorama(hostRef.current, {
              position: data.location.latLng,
              pov: { heading, pitch: 0 },
              zoom: 0,
              addressControl: false,
              fullscreenControl: true,
              motionTracking: false,
              motionTrackingControl: false,
              panControl: true,
              zoomControl: true,
              linksControl: true,          // the arrows that let you walk
              clickToGo: true,
            });
          }
          setStatus("ok");
        },
      );
    }
    start();
    return () => { cancelled = true; };
  }, [lat, lng, hasCoord]);

  const showStreet = tab === "street" && status !== "none";
  const tabs = [
    status !== "none" && { k: "street", label: "Street View" },
    photoUrl && { k: "photo", label: "Photo" },
  ].filter(Boolean);

  if (!hasCoord && !photoUrl) return null;

  return (
    <section className={`${B.CARD} overflow-hidden flex-shrink-0`}>
      <header className="px-4 py-2 border-b border-[#3a3a3a] flex items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          {tabs.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-2.5 py-1 rounded-lg text-[11px] transition ${
                tab === t.k ? "bg-[#2c2c2c] text-[#f4f4f4]" : "text-[#8a8a8a] hover:text-[#b4b4b4]"}`}>
              {t.label}
            </button>
          ))}
          {status === "loading" && (
            <span className="text-[10.5px] text-[#7a7a7a] ml-1">finding a panorama…</span>
          )}
          {/* The panorama IS walkable, but nothing on screen says so: the
              arrows sit flat on the road and disappear behind a tree or a
              sign about as often as not, and clicking anywhere that is not
              navigable does nothing at all - which reads as broken rather
              than as "not that way". One line of text costs nothing and
              saves the guess. */}
          {status === "ok" && tab === "street" && (
            <span className="text-[10.5px] text-[#7a7a7a] ml-1">
              drag to look · arrows on the road to walk
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {address && <span className="text-[10.5px] text-[#8a8a8a] truncate max-w-[22rem]">{address}</span>}
          {!readOnly && onChangePhoto && (
            <button onClick={onChangePhoto}
              className="text-[10.5px] text-[#8a8a8a] hover:text-[#f4f4f4] px-2 py-1 rounded-lg hover:bg-[#2c2c2c]">
              {photoUrl ? "Change photo" : "Add photo"}
            </button>
          )}
        </div>
      </header>

      <div className="relative" style={{ height }}>
        {/* The panorama host stays mounted whatever tab is showing. Unmounting
            it would destroy the panorama and force a rebuild - and a rebuilt
            one costs another WebGL context. It is hidden, not removed. */}
        <div ref={hostRef} className="absolute inset-0"
             style={{ visibility: showStreet ? "visible" : "hidden" }} />

        {tab === "photo" && photoUrl && (
          <a href={photoUrl} target="_blank" rel="noopener noreferrer"
             className="absolute inset-0 block bg-[#1c1c1c]">
            <img src={photoUrl} alt={address || "Property"} className="w-full h-full object-cover" />
          </a>
        )}

        {status === "none" && tab === "street" && (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
            <p className="text-[12px] text-[#8a8a8a] leading-relaxed">
              {hasCoord
                ? "Google has no outdoor Street View imagery at this coordinate."
                : "This deal has no coordinate, so there is nothing to look at yet."}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
